import React, { ComponentType, useMemo } from 'react'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { Spinner, Tab } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { InputParametersHierarchyInfo } from './InputParametersHierarchyInfo'
import { useInputParametersConfig } from '../../../../components/InputParameterHierarchy/hooks'
import { InputParameterHierarchyType, MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../../../../components/InputParameterHierarchy/model'
import { useGlobalFormPresetInputParameters } from './hooks'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'

export interface PresetInputParametersHierarchyConfigModalProps {
  show: boolean
  onHide: () => void
  preset: AiFeaturePreset
  GlobalConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  CurrentUserConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  TeamMembersConfig?: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
}

export const PresetInputParametersHierarchyConfigModal: React.FC<PresetInputParametersHierarchyConfigModalProps> = ({
  show,
  onHide,
  preset,
  GlobalConfig,
  CurrentUserConfig,
  TeamMembersConfig,
}) => {
  const { ipHierarchyEnabled, hasIpHierarchyAdminScope, showTabs, teamMembersLoading, hasTeamMembers } = useInputParametersConfig()
  const { presetInputParameters } = useGlobalFormPresetInputParameters(preset)

  const accountId = useAppSelector(selectAccountId)
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const lockedIPHierarchyTypes = accountResponse?.data?.enterpriseConfig?.lockedIPHierarchyTypes ?? []
  const isLockedByEnterprise = lockedIPHierarchyTypes.some((entry) => entry.typeId === preset.id)

  const configProps = useMemo(() => {
    return {
      type: InputParameterHierarchyType.AI_FEATURE_PRESET,
      typeId: preset.id,
      inputParametersSchema: preset.inputParametersSchema,
      defaultInputParameters: presetInputParameters,
      onHide,
      showCustomizationSetting: isLockedByEnterprise ? false : hasTeamMembers || (hasIpHierarchyAdminScope && ipHierarchyEnabled),
      ipHierarchyEnabled,
      lockedByEnterprise: isLockedByEnterprise,
    }
  }, [
    preset.id,
    preset.inputParametersSchema,
    presetInputParameters,
    onHide,
    isLockedByEnterprise,
    hasTeamMembers,
    hasIpHierarchyAdminScope,
    ipHierarchyEnabled,
  ])

  const userTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Own
    </div>
  )

  const teamMembersTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Team Members
    </div>
  )

  const enterpriseLockedInputParametersInfo = isLockedByEnterprise ? (
    <InputParametersHierarchyInfo icon='bi-exclamation-square' message='Preset configurations are managed at the enterprise level.' />
  ) : null

  const tabConfig = (
    <Tabs defaultActiveKey={hasIpHierarchyAdminScope ? 'global' : 'currentUser'} id='preset-config-tabs' className='d-flex mb-3' mountOnEnter unmountOnExit>
      <Tab eventKey='global' title='Account Level'>
        <InputParametersHierarchyInfo icon='bi-exclamation-square' message='These settings will apply to everyone unless they have their own settings.' />
        {enterpriseLockedInputParametersInfo}
        <GlobalConfig {...configProps} />
      </Tab>
      <Tab eventKey='currentUser' title={userTitle}>
        <InputParametersHierarchyInfo icon='bi-info-square' message='These settings will only apply to you.' />
        {enterpriseLockedInputParametersInfo}
        <CurrentUserConfig {...configProps} />
      </Tab>
      {TeamMembersConfig && (
        <Tab eventKey='teamMembers' title={teamMembersTitle}>
          <InputParametersHierarchyInfo icon='bi-info-square' message='Manage settings for your team members.' />
          {enterpriseLockedInputParametersInfo}
          <TeamMembersConfig {...configProps} />
        </Tab>
      )}
    </Tabs>
  )

  const singleConfig = ipHierarchyEnabled ? <CurrentUserConfig {...configProps} /> : <GlobalConfig {...configProps} />

  const body = (
    <div className='form-disabled-config'>
      <h4 className='text-center mb-5'>{`Customize ${preset.name}`}</h4>
      {teamMembersLoading ? (
        <div className='d-flex align-items-center justify-content-center p-2'>
          <Spinner size={'sm'} />
        </div>
      ) : showTabs ? (
        tabConfig
      ) : (
        <>
          {enterpriseLockedInputParametersInfo}
          {singleConfig}
        </>
      )}
    </div>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<></>} body={body} footer={<></>} />
}
